import React, {useEffect, useState} from 'react';
import {
  ActivityIndicator,
  FlatList,
  Image,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import {useFocusEffect} from '@react-navigation/native';
import {observer} from 'mobx-react-lite';

import FeedProductTileCard from '../../components/FeedProductTileCard';
import ContentView from '../../components/ui-controls/layout/ContentView';
import Text from '../../components/ui-controls/Text';
import {useWebScreenDimensions} from '../../hooks/useWebScreenDimensions';
import {AirtablePromotionItem} from '../../models/airtable';
import {AppRoute} from '../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {useStores} from '../../stores/use-stores';
import theme from '../../style/theme';
import {getFeedDiscoverDetailGraphics} from '../../utils/feedAndDiscover/media';
import {getHeroImageDetail} from '../../utils/heroImage';
import {isBmOrSnsOrDev} from '../../utils/tenants';

const HEADLINE_VIDEO = 'HEADLINE_VIDEO';

const BrandDetailScreen = observer(
  ({navigation, route}: RouteNavigatorScreenProps<AppRoute.BRAND>) => {
    const {
      brandsStore,
      promotionsStore: activationsStore,
      rangeStore,
    } = useStores();
    const [linkedProducts, setLinkedProducts] = useState(null);
    const brand = route.params.brand;

    useEffect(() => {
      async function loadLinkedProducts() {
        const linked = await brandsStore.loadLinkedProductsForBrand(brand);
        setLinkedProducts(linked);
      }
      if (brand?.brand && rangeStore.departments.length) {
        loadLinkedProducts();
      }
    }, [brand, brandsStore, rangeStore.departments.length]);

    const [headlineVideoPaused, setHeadlineVideoPaused] = useState(true);
    const [articleVideoPaused, setArticleVideoPaused] = useState(true);
    const [youtubeVideoPaused, setYoutubeVideoPaused] = useState(true);

    const {isWebDesktopScreen} = useWebScreenDimensions();

    // This is to stop the video playing whenever we navigate away.
    useFocusEffect(
      React.useCallback(() => {
        // Do something when the screen is focused
        // component.current.startAutoplay(false);

        return () => {
          // Do something when the screen is unfocused
          // Useful for cleanup functions
          setHeadlineVideoPaused(true);
          setArticleVideoPaused(true);
          setYoutubeVideoPaused(true);
        };
      }, []),
    );

    const getImage = (detail, vidType, margin) => {
      return getFeedDiscoverDetailGraphics(
        detail,
        vidType,
        margin,
        null,
        setHeadlineVideoPaused,
        headlineVideoPaused,
        articleVideoPaused,
        youtubeVideoPaused,
      );
    };

    const promotions = activationsStore.promotions.filter(activation =>
      brand?.promotions?.includes(activation.id),
    );

    const handlePromotionPress = (promotion: AirtablePromotionItem) =>
      navigation.navigate(AppRoute.PROMOTION, {
        promotion: promotion,
        brand: brand,
      });

    return (
      <ContentView>
        <ScrollView
          showsVerticalScrollIndicator={false}
          showsHorizontalScrollIndicator={false}>
          <View style={styles.container}>
            {getImage(brand?.heroImage, HEADLINE_VIDEO)}
            <View style={styles.textCard}>
              <Text style={styles.headline}>{brand?.headline}</Text>
              <Text style={styles.copy}>{brand?.copy?.trimEnd()}</Text>
            </View>
          </View>
          {promotions.map(promotion => {
            const image = getHeroImageDetail(promotion, isWebDesktopScreen);
            return (
              <View key={promotion.id} style={styles.promotionImageHolder}>
                <Pressable onPress={() => handlePromotionPress(promotion)}>
                  <Image
                    source={{uri: image?.url}}
                    style={
                      isWebDesktopScreen
                        ? styles.promotionImageWebDesktop
                        : styles.promotionImage
                    }
                  />
                  <View style={styles.textHolder}>
                    <Text style={styles.headline}>
                      {promotion.tileHeadline ||
                        promotion.bannerHeadline ||
                        promotion.headline}
                    </Text>
                    {!!(promotion.firstCopy || promotion.description) && (
                      <Text
                        style={styles.copyStyle}
                        numberOfLines={2}
                        adjustsFontSizeToFit={false}>
                        {promotion.firstCopy || promotion.description}
                      </Text>
                    )}
                  </View>
                </Pressable>
              </View>
            );
          })}

          {linkedProducts &&
          Object.keys(linkedProducts).length &&
          !brandsStore.loadingLinked ? (
            Object.entries(linkedProducts).map(([key, value], index) => (
              <View
                style={styles.productHolder}
                key={`${key.replace(' ', '')}-${index}`}>
                <Text style={styles.categoryName}>{key}</Text>
                <FlatList
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  showsVerticalScrollIndicator={false}
                  style={styles.flatList}
                  data={value}
                  keyExtractor={item => item.id}
                  renderItem={({item}) => (
                    <FeedProductTileCard
                      key={item.id}
                      product={item}
                      source={navigation.getId() as AppRoute}
                    />
                  )}
                />
              </View>
            ))
          ) : !brandsStore.loadingLinked ? null : (
            <ActivityIndicator size="large" style={styles.loadingIndicator} />
          )}
        </ScrollView>
      </ContentView>
    );
  },
);

export default BrandDetailScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  imageContainer: {
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 5,
    },
    shadowOpacity: 0.29,
    shadowRadius: 4.65,
    elevation: 7,
  },
  topMargin: {
    marginTop: 16,
  },
  imageResponsive: {
    width: '100%',
    height: undefined,
    aspectRatio: 1,
  },

  textCard: {
    marginTop: 16,
  },
  headline: {
    fontSize: 20,
    color: theme.colour.text.heading.primary,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  copy: {
    fontSize: 16,
  },
  videoContainer: {
    borderRadius: 4,
    width: '100%',

    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 5,
    },
    shadowOpacity: 0.29,
    shadowRadius: 4.65,
    elevation: 7,
  },
  video: {
    borderRadius: 4,
    width: '100%',
    aspectRatio: 1920 / 1080,
  },
  youtube: {
    width: '100%',
    aspectRatio: 1920 / 1080,
    marginTop: 16,
    borderRadius: 4,
  },
  productHolder: {
    marginTop: 24,
    marginBottom: 4,
  },
  categoryName: {
    fontSize: 16,
    fontWeight: '400',
    color: theme.colour.text.heading.primary,
    marginBottom: 8,
    paddingLeft: 8,
    textTransform: 'uppercase',
  },
  flatList: {
    overflow: 'visible',
  },
  promotionImageHolder: {
    marginTop: 24,
    marginHorizontal: 8,
    backgroundColor: 'white',
    borderRadius: 4,
    borderWidth: 0.5,
    borderColor: '#CCC',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 5,
    },
    shadowOpacity: 0.29,
    shadowRadius: 4.65,
    elevation: 7,
    overflow: 'visible',
  },
  promotionImage: {
    width: '100%',
    aspectRatio: 760 / (300 + 200),
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
  },
  promotionImageWebDesktop: {
    width: '100%',
    aspectRatio: 38 / 15,
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
  },
  textHolder: {
    width: '100%',
    backgroundColor: theme.colour.general.secondary,
    borderBottomLeftRadius: 4,
    borderBottomRightRadius: 4,
    paddingHorizontal: 12,
    paddingVertical: 12,
  },
  loadingIndicator: {
    color: theme.colour.activityIndicator.primary,
    marginTop: 32,
  },
});
